import { useState } from "react";
import { useIntl } from "react-intl";
import { Accordion, AccordionSummary, AccordionDetails, Typography, Stack, Box, useTheme } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

interface PlatformInstructionsProps {
	feedUrl: string;
}

export const PlatformInstructions = ({ feedUrl }: PlatformInstructionsProps) => {
	const theme = useTheme();
	const intl = useIntl();
	const [expanded, setExpanded] = useState<string | false>(false);

	const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpanded(isExpanded ? panel : false);
	};

	const instructions = [
		{
			id: "google",
			titleKey: "CALENDAR_SUBSCRIBE.platform.google.title",
			steps: [
				"CALENDAR_SUBSCRIBE.platform.google.step1",
				"CALENDAR_SUBSCRIBE.platform.google.step2",
				"CALENDAR_SUBSCRIBE.platform.google.step3",
				"CALENDAR_SUBSCRIBE.platform.google.step4",
				"CALENDAR_SUBSCRIBE.platform.google.step5",
				"CALENDAR_SUBSCRIBE.platform.google.step6",
			],
		},
		{
			id: "apple",
			titleKey: "CALENDAR_SUBSCRIBE.platform.apple.title",
			steps: [
				"CALENDAR_SUBSCRIBE.platform.apple.step1",
				"CALENDAR_SUBSCRIBE.platform.apple.step2",
				"CALENDAR_SUBSCRIBE.platform.apple.step3",
				"CALENDAR_SUBSCRIBE.platform.apple.step4",
				"CALENDAR_SUBSCRIBE.platform.apple.step5",
				"CALENDAR_SUBSCRIBE.platform.apple.step6",
			],
		},
		{
			id: "outlook-desktop",
			titleKey: "CALENDAR_SUBSCRIBE.platform.outlook_desktop.title",
			steps: [
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step1",
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step2",
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step3",
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step4",
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step5",
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step6",
				"CALENDAR_SUBSCRIBE.platform.outlook_desktop.step7",
			],
		},
		{
			id: "outlook-web",
			titleKey: "CALENDAR_SUBSCRIBE.platform.outlook_web.title",
			steps: [
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step1",
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step2",
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step3",
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step4",
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step5",
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step6",
				"CALENDAR_SUBSCRIBE.platform.outlook_web.step7",
			],
		},
		{
			id: "other",
			titleKey: "CALENDAR_SUBSCRIBE.platform.other.title",
			steps: [
				"CALENDAR_SUBSCRIBE.platform.other.step1",
				"CALENDAR_SUBSCRIBE.platform.other.step2",
				"CALENDAR_SUBSCRIBE.platform.other.step3",
				"CALENDAR_SUBSCRIBE.platform.other.step4",
				"CALENDAR_SUBSCRIBE.platform.other.step5",
				"CALENDAR_SUBSCRIBE.platform.other.step6",
				"CALENDAR_SUBSCRIBE.platform.other.step7",
				"CALENDAR_SUBSCRIBE.platform.other.step8",
			],
		},
	];

	return (
		<Stack>
			{instructions.map((instruction) => (
				<Accordion
					key={instruction.id}
					expanded={expanded === instruction.id}
					onChange={handleChange(instruction.id)}
					sx={{
						px: 4,
						py: 2,
						"&:before": {
							display: "none",
						},
						boxShadow: "none",
						border: `1px solid ${theme.palette.divider}`,
						"&:not(:last-child)": {
							borderBottom: 0,
						},
						"&:first-of-type": {
							borderTopLeftRadius: "0.8rem",
							borderTopRightRadius: "0.8rem",
						},
						"&:last-of-type": {
							borderBottomLeftRadius: "0.8rem",
							borderBottomRightRadius: "0.8rem",
						},
					}}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						sx={{
							minHeight: "4.8rem",
							"&.Mui-expanded": {
								minHeight: "4.8rem",
							},
						}}
					>
						<Typography fontWeight={600} fontSize="1.6rem">
							{intl.formatMessage({ id: instruction.titleKey })}
						</Typography>
					</AccordionSummary>
					<AccordionDetails>
						<Stack spacing={3}>
							{instruction.steps.map((stepKey, index) => (
								<Box key={index} display="flex" gap={1}>
									<Typography
										component="span"
										fontWeight={600}
										fontSize="1.6rem"
										color="text.secondary"
										sx={{ minWidth: "2.4rem" }}
									>
										{index + 1}.
									</Typography>
									<Typography
										fontSize="1.6rem"
										color="text.secondary"
										sx={{
											wordBreak: "break-word",
											overflowWrap: "break-word",
											flex: 1,
										}}
									>
										{intl.formatMessage({ id: stepKey }, { feedUrl })}
									</Typography>
								</Box>
							))}
						</Stack>
					</AccordionDetails>
				</Accordion>
			))}
		</Stack>
	);
};

